// =====================================================================================
// INVRT Radar — web console — first-membership claim (W6-03-B)
//
// The two claim endpoints are the ONE authenticated surface a membership-less user can
// reach: neither sends X-Org-Id and the server calls no resolveOrgScope() for them —
// `requiresOrg: false` mirrors that contract exactly (lib/api.ts would otherwise refuse
// locally before the request left the browser, since no org is chosen yet).
// =====================================================================================

import {
  useMutation,
  type UseMutationResult,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useSession } from "@/auth/SessionProvider";
import { useApiClient } from "./useApi";

export interface ClaimableOrg {
  org_id: string;
  org_name: string;
  approved_at: string;
}

export interface ClaimableResponse {
  claimable: ClaimableOrg[];
}

export interface ClaimResponse {
  org_id: string;
  is_org_admin: boolean;
  claimed: boolean;
}

/** The organisations this sign-in address may activate. Server-matched; never asserted here. */
export function useClaimableOrgs(): UseQueryResult<ClaimableResponse> {
  const api = useApiClient();
  const { session } = useSession();

  return useQuery({
    queryKey: ["claimable", session?.user.id ?? null],
    queryFn: () => api.get<ClaimableResponse>("/account/claimable", { requiresOrg: false }),
    enabled: Boolean(session),
  });
}

/**
 * Activates one organisation. The server re-verifies every rule from scratch — the posted
 * org_id only says which of the CALLER'S OWN matches to claim, and grants nothing by
 * itself. Idempotent server-side on the memberships primary key.
 */
export function useClaimOrg(): UseMutationResult<ClaimResponse, unknown, { org_id: string }> {
  const api = useApiClient();

  return useMutation({
    mutationFn: (body: { org_id: string }) =>
      api.post<ClaimResponse>("/account/claim", body, { requiresOrg: false }),
  });
}
